import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CompanyDetailsInterface, CompanyHoursHookInterface } from '../../services/exports/Interfaces';
import { StoreInterface } from '../../store/types';
import { ORDERS_PAUSED_REASON, ORDER_METHODS } from '../../services/exports/Constants';
import useCompanyBusinessTime from './company-business-time/useCompanyBusinessTime';

interface Props {
  data?: CompanyDetailsInterface;
  orderMethod?: ORDER_METHODS;
  skipHolidays?: boolean;
}

export default function useCompanyHours(props: Props = {}): CompanyHoursHookInterface {
  const cachedOrder = useSelector((state: StoreInterface) => state.order.cached_order);
  const companyData = useSelector((state: StoreInterface) => state.initialData.company);
  const company = props?.data ?? companyData;
  const now = moment();
  const orderMethod = props.orderMethod ?? cachedOrder?.method ?? ORDER_METHODS.PICKUP;
  const pickupBusinessTime = useCompanyBusinessTime({
    orderMethod: ORDER_METHODS.PICKUP,
    company,
    skipHolidays: props.skipHolidays,
  });
  const deliveryBusinessTime = useCompanyBusinessTime({
    orderMethod: ORDER_METHODS.DELIVERY,
    company,
    skipHolidays: props.skipHolidays,
  });
  const businessTime = {
    [ORDER_METHODS.DELIVERY]: deliveryBusinessTime,
    [ORDER_METHODS.PICKUP]: pickupBusinessTime,
    [ORDER_METHODS.ROOM_SERVICE]: pickupBusinessTime,
  };
  const defaultBusinessTime = businessTime[orderMethod] ?? pickupBusinessTime;

  const orderTimeAvailable = useMemo(
    () =>
      cachedOrder && businessTime[cachedOrder.method]?.isBusinessTime(cachedOrder?.scheduled_for ?? now.toISOString()),
    [businessTime, cachedOrder?.method, cachedOrder?.scheduled_for],
  );

  const open = defaultBusinessTime.isBusinessTime(now.toISOString());
  const currentSchedule =
    orderMethod === ORDER_METHODS.PICKUP ? company?.current_work_schedule : company?.current_delivery_schedule;
  const openWholeDay = defaultBusinessTime.isOpenWholeDay(now.toISOString());
  const nextBusinessTime = defaultBusinessTime.getNextBusinessTime(now.toISOString());

  const _willOpenToday = now.isSame(nextBusinessTime, 'day');

  const orderArePausedTillEndOfDay = now.clone().endOf('day').isSame(moment(company?.orders_paused_until));

  const opensStartingTomorrow = {
    status: !open && (!_willOpenToday || orderArePausedTillEndOfDay),
    time: nextBusinessTime,
  };

  const opensToday = {
    status: _willOpenToday,
    time: nextBusinessTime,
  };

  const ordersArePaused = company?.orders_paused_until && moment(company?.orders_paused_until).isAfter(now);
  const ordersArePausedManually = ordersArePaused && company?.orders_pause_reason === ORDERS_PAUSED_REASON.MANUAL;

  const closedFrom = company?.closed_from ? moment(company?.closed_from) : null;
  const closedUntil = company?.closed_until ? moment(company?.closed_until) : null;

  const pickupClosed = !pickupBusinessTime.isBusinessTime(now.toISOString());
  const deliveryClosed = !deliveryBusinessTime.isBusinessTime(now.toISOString());

  return {
    open,
    openWholeDay,
    closedFrom,
    closedUntil,
    orderTimeAvailable,
    pickupClosed,
    deliveryClosed,
    opensToday,
    opensStartingTomorrow,
    ordersPausedUntil: company?.orders_paused_until,
    orderArePausedTillEndOfDay,
    ordersPausedReason: company?.orders_pause_reason,
    ordersArePaused,
    ordersArePausedManually,
    currentSchedule,
  };
}
